// lib
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { batch } from "react-redux";
import { useHistory } from "react-router-dom";
import queryString from "query-string";
import { any, pick } from "ramda";
// src
import Table from "containers/plannings/table/Table";
import NotFound from "components/plannings/routes/NotFound";
import { getPresetId } from "utils/presets";
import config from "config";
import { PresetType } from "config/constants";

const PLAIN_ID_REGEX = /^[0-9]+$/i;

const ALLOWED_TYPES = ["rooms", "groups", "courses", "people", "presets", "pack"];
const PLAIN_ID_TYPES = ["rooms", "groups", "courses", "projects"];
const ALLOWED_PACK_RESOURCE_KEYS = ["rooms", "groups", "courses", "people", "projects"];
const REQUIRED_PACK_KEY = "name";
const checkIfAllowed = (type, id) => {
  // deeplink types whitelisting
  if (!ALLOWED_TYPES.includes(type)) return false;
  // controls id of resource, for instance: rejects if room id contains a letter
  if (PLAIN_ID_TYPES.includes(type) && !PLAIN_ID_REGEX.test(id)) return false;
  if (type === "pack") {
    const params = queryString.parse(id, { arrayFormat: "bracket" });
    for (const resourceType of Object.keys(params)) {
      // controls ids of all resources in pack
      if (PLAIN_ID_TYPES.includes(resourceType)) {
        if (any((rId) => !PLAIN_ID_REGEX.test(rId), params[resourceType])) return false;
      }
    }
    // should contain required parameters for pack
    return Object.keys(params).includes(REQUIRED_PACK_KEY);
  }
  return true;
};

const DeepLink = ({
  // router
  id,
  isLight,
  type,
  url,
  // action props
  manageAddToLocals,
  computeDisplay,
  exclusiveLightEnable,
  fetchEnableds,
}) => {
  const isAllowed = checkIfAllowed(type, id);
  const [doProceed, setDoProceed] = useState(false);
  useEffect(() => setDoProceed(true), [type, id]);
  const history = useHistory();
  const isPack = type === "pack";

  // doProceed (&setTimeout) filters unwanted router *match* updates
  // those unwanted updates create a re-render infinite loop
  if (doProceed && isAllowed) {
    setTimeout(() => {
      let preset;
      if (type === "presets") {
        preset = { id };
      } else if (isPack) {
        const rawParams = queryString.parse(id, { arrayFormat: "bracket" });
        const params = pick(ALLOWED_PACK_RESOURCE_KEYS, rawParams);
        const rawPreset = {
          params,
          type: PresetType.INSTANT,
        };
        preset = {
          ...rawPreset,
          id: getPresetId(rawPreset),
          name: rawParams.name,
        };
      } else {
        // else = resource type
        const rawPreset = { params: { [type]: [id] }, type: PresetType.RESOURCE };
        preset = {
          ...rawPreset,
          id: getPresetId(rawPreset),
          share: `${config.root}${url}`,
        };
      }
      batch(() => {
        // first thing: refresh display from hydrated state (fetchEnableds will recompute)
        computeDisplay();
        if (isLight) {
          exclusiveLightEnable(preset);
          if (isPack) {
            history.push("/light");
          } else {
            fetchEnableds();
          }
        } else {
          manageAddToLocals(preset);
          history.push("/");
        }
      });
    }, 0);
    setDoProceed(false);
  }

  return isAllowed ? <Table /> : <NotFound />;
};

DeepLink.propTypes = {
  // router
  id: PropTypes.string.isRequired,
  isLight: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  // action props
  manageAddToLocals: PropTypes.func.isRequired,
  computeDisplay: PropTypes.func.isRequired,
  exclusiveLightEnable: PropTypes.func.isRequired,
  fetchEnableds: PropTypes.func.isRequired,
};

export default DeepLink;
